import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useFilterStore } from '@/features/filters/store/filter-store';
import { countryCountsApi } from '../api/country-counts-api';

/**
 * Fetches per-country charge metrics for the country overlay.
 *
 * Reactive to the operator/team/promotion/speed filters (same dimensions the
 * by_country endpoint honours). Only enabled while the overlay is visible so we
 * don't fetch at marker zoom levels.
 *
 * @param enabled whether the country overlay is currently showing
 */
export function useCountryCounts(enabled: boolean) {
  // Subscribe to the filters that affect the result so the query re-runs on change.
  useFilterStore((s) => s.selectedOperatorIds);
  useFilterStore((s) => s.selectedTeamIds);
  useFilterStore((s) => s.selectedPromotionIds);
  useFilterStore((s) => s.showSlow);
  useFilterStore((s) => s.showFast);
  useFilterStore((s) => s.showUltraFast);
  const getApiFilters = useFilterStore((s) => s.getApiFilters);

  const filters = getApiFilters();

  return useQuery({
    // Key only on the params the endpoint actually uses — availability filters
    // don't affect by_country results, so they shouldn't bust the cache.
    queryKey: [
      'country-counts',
      filters.operator_ids ?? null,
      filters.team_ids ?? null,
      filters.promotion_ids ?? null,
      filters.min_kw ?? null,
      filters.max_kw ?? null,
    ],
    queryFn: () => countryCountsApi.getByCountry(filters),
    enabled,
    // Keep the previous country coverage visible while a filter-change refetch
    // is in flight. Without this, `data` drops to undefined between query keys,
    // which flips the overlay to "nothing loaded" and momentarily un-hides every
    // country (a flash) before the new coverage re-hides them.
    placeholderData: keepPreviousData,
    staleTime: 60_000, // mirrors the BE cache TTL
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}
